import { loadPdfDocument, pdfOps } from "./utils";
import { loadJbig2 } from "./jbig2/jbig2";
import { browserImageCodec, type ImageCodec } from "./imageRecompress";
import type { CanvasLike, OpenPdf, PageEnv } from "./pageLevels";
import { compressPagesInLevelsWith, type PageLevelOptions } from "./pageLevels";
import type { LadderEnv } from "./compressLadder";

/**
 * The browser's side of Compress PDF. The ladder itself (levels, page grid, piece assembly, the stop-at-goal rule) is in
 * compressLadder.ts and pageLevels.ts, shared with the API; this file says how a browser opens a PDF, makes a canvas and
 * writes a JPEG.
 */

export { pageFootprints } from "./pageFootprints";
export { emptyPages, QUADRANT_LEVEL, SIXTEENTH_LEVEL, BW_LEVELS, CLEAN_LEVEL, type LevelCheckpoint, type PageLevelOptions } from "./pageLevels";

async function canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Canvas encoding failed"))), "image/jpeg", quality);
  });
}

async function renderPageCanvas(pdfjsDoc: Awaited<ReturnType<typeof loadPdfDocument>>, pageNumber: number, scale: number): Promise<HTMLCanvasElement> {
  const page = await pdfjsDoc.getPage(pageNumber);
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(viewport.width));
  canvas.height = Math.max(1, Math.round(viewport.height));
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported in this browser");
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  await page.render({ canvasContext: ctx, viewport, canvas, intent: "print" }).promise;
  return canvas;
}

/** How a browser opens, renders and encodes for the shared page levels. */
export function browserPageEnv(file: File): PageEnv {
  return {
    async open(): Promise<OpenPdf> {
      const doc = await loadPdfDocument(file);
      return {
        numPages: doc.numPages,
        getPage: (n) => doc.getPage(n) as unknown as ReturnType<OpenPdf["getPage"]>,
        renderCanvas: (n, scale) => renderPageCanvas(doc, n, scale) as Promise<CanvasLike>,
      };
    },
    makeCanvas(width, height) {
      const c = document.createElement("canvas");
      c.width = width;
      c.height = height;
      return c as unknown as CanvasLike;
    },
    async jpeg(canvas, quality) {
      return new Uint8Array(await (await canvasToBlob(canvas as unknown as HTMLCanvasElement, quality)).arrayBuffer());
    },
    loadJbig2: () => loadJbig2(),
  };
}

/** The whole environment the compress ladder needs, for one file in the browser. */
export function browserLadderEnv(file: File, codec: ImageCodec = browserImageCodec): LadderEnv {
  return { ...browserPageEnv(file), codec, ops: pdfOps };
}

/** Turns the remaining pages into pictures level by level (see pageLevels.ts), in the browser. */
export function compressPagesInLevels(file: File, opts: PageLevelOptions) {
  return compressPagesInLevelsWith(browserPageEnv(file), opts);
}
